import { Component, Inject, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { SidebarNavMenuItem } from '../../decorators/sidebar-nav-menu-item';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../injection-tokens/entity-service-tokens';
import { IClassesEntityService } from '../../interfaces/entities/classes-entity-service.interface';
import { ErrorHandlerService } from '../../decoration-base/error-handler.service';
import { Log, IShortLogger } from '../../services/logger.service';
import { UserPrefsService } from '../../services/user-prefs.service';
import { AuthService } from '../../services/auth.service';
import { isParticipant } from '../../utils/auth.utils';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../shared/pagination-options';
import {
  TemplateSubBulkAddDialogComponent,
  TemplateSubBulkAddDialogResult,
} from './template-sub-bulk-add-dialog.component';
import {
  DeleteConfirmDialogComponent,
  DeleteConfirmDialogData,
} from './delete-confirm-dialog.component';

/** 5-band colour palette for the template-subscription quota bar. */
const TMPL_BAND_COLORS = {
  green: '#43a047',
  yellow: '#fbc02d',
  orange: '#fb8c00',
  red: '#e53935',
  grey: '#9e9e9e',
} as const;

/** UserPrefs page key for this component's preferences. */
const TMPL_SUBS_BANNER_PAGE = 'templateSubscriptions';

@SidebarNavMenuItem({
  route: '/template-vm-subscriptions',
  faIcon: 'dns',
  name: 'TemplateVMSubscriptions',
  order: 1100,
  businessProcessesName: 'TemplateVMSubscriptions',
})
@Component({
  selector: 'app-vm-template-subscriptions',
  templateUrl: './vm-template-subscriptions.component.html',
  styleUrls: ['./vm-template-subscriptions.component.css'],
})
export class VMTemplateSubscriptionsComponent implements OnInit, AfterViewInit {
  private readonly logger: IShortLogger<VMTemplateSubscriptionsComponent> =
    Log.create(VMTemplateSubscriptionsComponent);

  // ── Data sources ──────────────────────────────────────────────────────────
  dataSource = new MatTableDataSource<any>([]);
  /** Quota + display name merged into a single column — no MaxVms because template
   *  subscriptions are not bound to a specific VM SKU. */
  readonly displayedColumns = [
    'displayName',
    'subscriptionId',
    'location',
    'quota',
    'actions',
  ];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // ── UI state ──────────────────────────────────────────────────────────────
  isLoading = false;
  isRefreshingAll = false;
  searchTerm = '';

  // ── Banner state ──────────────────────────────────────────────────────────
  bannerVisible = false;
  bannerDismissForever = false;

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private classService: IClassesEntityService,
    private errorHandler: ErrorHandlerService,
    private userPrefs: UserPrefsService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.loadSubscriptions();

    // Show banner only for operator users who have not permanently dismissed it.
    this.bannerVisible =
      !isParticipant(this.authService.getCurrentUserValue()) &&
      !this.userPrefs.isBannerDismissed(TMPL_SUBS_BANNER_PAGE);

    this.dataSource.filterPredicate = (row: any, filter: string) =>
      (row.SubscriptionDisplayName ?? '').toLowerCase().includes(filter) ||
      (row.SubscriptionId ?? '').toLowerCase().includes(filter) ||
      (row.Location ?? '').toLowerCase().includes(filter);
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  onSearchChange(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
  }

  dismissBanner(): void {
    this.bannerVisible = false;
    if (this.bannerDismissForever) {
      this.userPrefs.dismissBanner(TMPL_SUBS_BANNER_PAGE);
    }
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  loadSubscriptions(): void {
    this.isLoading = true;
    this.classService.getAllTemplateSubscription().subscribe({
      next: (all: any[]) => {
        this.dataSource.data = (all ?? []).map(s => this.normalizeSubscription(s));
        this.isLoading = false;
        this.logger.i('Loaded subscriptions:', this.dataSource.data.length);
        // Silently refresh stale rows in the background.
        this.backgroundRefreshStaleRows();
      },
      error: (err: any) => {
        this.isLoading = false;
        this.errorHandler.handleError(err, 'Failed to load subscriptions');
      },
    });
  }

  // ── Quota refresh ─────────────────────────────────────────────────────────

  /** Refreshes quota for every row in parallel via live Azure API calls. */
  refreshAllQuota(): void {
    const rows = this.dataSource.data;
    if (!rows.length || this.isRefreshingAll) return;

    this.isRefreshingAll = true;
    rows.forEach(r => (r.isRefreshing = true));
    this.dataSource.data = [...rows];

    let completed = 0;
    const done = (): void => {
      completed++;
      if (completed >= rows.length) {
        this.isRefreshingAll = false;
        this.snackBar.open('All quotas refreshed', 'Dismiss', { duration: 2500 });
      }
    };

    rows.forEach(row => {
      if (!row.Id) { done(); return; }
      this.classService.refreshTemplateSubscriptionQuota(row.Id).subscribe({
        next: (res: any) => {
          this.applyQuotaResponse(row, res);
          row.isRefreshing = false;
          this.dataSource.data = [...this.dataSource.data];
          done();
        },
        error: () => {
          row.isRefreshing = false;
          this.dataSource.data = [...this.dataSource.data];
          done();
        },
      });
    });
  }

  /** Refreshes quota for a single row via a live Azure API call. */
  refreshRowQuota(row: any): void {
    if (!row.Id || row.isRefreshing) return;
    row.isRefreshing = true;
    this.dataSource.data = [...this.dataSource.data];

    this.classService.refreshTemplateSubscriptionQuota(row.Id).subscribe({
      next: (res: any) => {
        this.applyQuotaResponse(row, res);
        row.isRefreshing = false;
        this.dataSource.data = [...this.dataSource.data];
        this.snackBar.open('Quota refreshed', 'Dismiss', { duration: 2000 });
      },
      error: (err: any) => {
        row.isRefreshing = false;
        this.dataSource.data = [...this.dataSource.data];
        this.snackBar.open(err?.error?.message ?? 'Quota refresh failed', 'Dismiss', {
          duration: 3000, panelClass: 'snack-error',
        });
      },
    });
  }

  private backgroundRefreshStaleRows(): void {
    this.dataSource.data
      .filter(r => r.IsStale)
      .forEach(row => {
        if (!row.Id) return;
        this.classService.refreshTemplateSubscriptionQuota(row.Id).subscribe({
          next: (res: any) => {
            this.applyQuotaResponse(row, res);
            this.dataSource.data = [...this.dataSource.data];
          },
          error: () => { /* silent */ },
        });
      });
  }

  private applyQuotaResponse(row: any, res: any): void {
    if (res?.coresLimit !== undefined) row.CoresLimit = res.coresLimit;
    if (res?.coresCurrentUsage !== undefined) row.CoresCurrentUsage = res.coresCurrentUsage;
    if (res?.pendingCores !== undefined) row.PendingCores = res.pendingCores;
    if (res?.quotaCheckedAt !== undefined) row.QuotaCheckedAt = res.quotaCheckedAt;
    if (res?.familyQuotaMap !== undefined) row.FamilyQuotaMap = res.familyQuotaMap;
    const prs = res?.providerRegistrationStatus ?? res?.ProviderRegistrationStatus;
    if (prs !== undefined) row.ProviderRegistrationStatus = prs;
    row.IsStale = false;
  }

  // ── Quota display helpers ─────────────────────────────────────────────────

  /**
   * Builds the hover tooltip text showing per-family quota breakdown.
   * Returns an empty string when no family data is available (tooltip is suppressed).
   */
  getFamilyTooltip(row: any): string {
    const map: Record<string, { limit: number; usage: number }> = row.FamilyQuotaMap;
    if (!map || Object.keys(map).length === 0) return '';
    return Object.entries(map)
      .map(([family, q]) => `${family}: ${q.usage}/${q.limit}`)
      .join('\n');
  }

  quotaUsagePercent(row: any): number {
    const limit = row.CoresLimit ?? 0;
    const used = row.CoresCurrentUsage ?? 0;
    return limit > 0 ? Math.round((used / limit) * 100) : 0;
  }

  /**
   * Returns the MDC progress-bar fill colour based on REMAINING quota capacity.
   * Bands (available fraction):
   *   ≥75% remaining → green  | ≥50% → yellow | ≥25% → orange | >0% → red | 0 / no data → grey
   */
  quotaBarColor(row: any): string {
    const limit = row.CoresLimit ?? 0;
    const used = row.CoresCurrentUsage ?? 0;
    if (limit <= 0) return TMPL_BAND_COLORS.grey;
    const avail = (limit - used) / limit;
    if (avail <= 0) return TMPL_BAND_COLORS.grey;
    if (avail < 0.25) return TMPL_BAND_COLORS.red;
    if (avail < 0.50) return TMPL_BAND_COLORS.orange;
    if (avail < 0.75) return TMPL_BAND_COLORS.yellow;
    return TMPL_BAND_COLORS.green;
  }

  formatQuotaCheckedAt(ts: string | undefined): string {
    return ts ? new Date(ts).toLocaleString() : '—';
  }

  // ── Add / Edit ────────────────────────────────────────────────────────────

  openBulkAddDialog(): void {
    const existingSubscriptions = this.dataSource.data.map((s: any) => ({
      subscriptionId: s.SubscriptionId,
      location: s.Location,
    }));
    (document.activeElement as HTMLElement)?.blur();
    const ref = this.dialog.open(TemplateSubBulkAddDialogComponent, {
      data: { existingSubscriptions },
      width: '600px',
      disableClose: false,
      autoFocus: 'first-tabbable',
      panelClass: 'bulk-add-dialog',
    });

    ref.afterClosed().subscribe((result: TemplateSubBulkAddDialogResult | null) => {
      // Regardless of how the dialog was dismissed (Done / X / backdrop),
      // reload subscriptions from the server to ensure:
      // 1. UI is always in sync with database (fixes X/backdrop dismiss issue)
      // 2. Subscription names are properly resolved by the server (fixes Unnamed issue)
      // 3. All metadata (quota, location, etc.) is fresh
      if (result?.added?.length) {
        this.snackBar.open(
          `${result.added.length} subscription(s) added`,
          'Dismiss',
          { duration: 4000 }
        );
      }
      // Always reload from server after dialog closes
      this.loadSubscriptions();
    });
  }



  // ── Delete ────────────────────────────────────────────────────────────────

  removeSubscription(subscription: any): void {
    const data: DeleteConfirmDialogData = {
      subscriptionId: subscription.Id,
      displayName: subscription.SubscriptionDisplayName,
    };

    (document.activeElement as HTMLElement)?.blur();
    const ref = this.dialog.open(DeleteConfirmDialogComponent, {
      data,
      width: '480px',
      disableClose: false,
      autoFocus: 'first-tabbable',
      panelClass: 'delete-confirm-dialog',
    });

    ref.afterClosed().subscribe((confirmed: boolean | null) => {
      if (confirmed === true) {
        this.dataSource.data = this.dataSource.data.filter(
          (s: any) => s.Id !== subscription.Id
        );
        this.snackBar.open('Subscription deleted', 'Dismiss', { duration: 4000 });
      } else if (confirmed === false) {
        this.snackBar.open('Failed to delete subscription', 'Dismiss', {
          duration: 5000,
          panelClass: 'snack-error',
        });
      }
    });
  }

  // ── View toggle ───────────────────────────────────────────────────────────

  // ── Normalisation ─────────────────────────────────────────────────────────
  // The list endpoint returns MongoDB field names (PascalCase via BsonDocument
  // preservation) while the create/update endpoints return C# anonymous objects
  // serialised as camelCase by the global JsonNamingPolicy. This helper maps
  // both shapes to the single PascalCase form the template expects.
  private normalizeSubscription(raw: any): any {
    const coresLimit = raw.CoresLimit ?? raw.coresLimit;
    const prs = raw.ProviderRegistrationStatus ?? raw.providerRegistrationStatus
      // Old DB rows have no stored status. If quota is still null treat as registering
      // so the badge shows instead of a plain dash, until the user refreshes.
      ?? (coresLimit == null ? 'registering' : 'registered');
    return {
      Id: raw.Id ?? raw.id,
      SubscriptionId: raw.SubscriptionId ?? raw.subscriptionId,
      SubscriptionDisplayName: raw.SubscriptionDisplayName ?? raw.subscriptionDisplayName,
      Location: raw.Location ?? raw.location,
      CoresLimit: coresLimit,
      CoresCurrentUsage: raw.CoresCurrentUsage ?? raw.coresCurrentUsage,
      PendingCores: raw.PendingCores ?? raw.pendingCores,
      QuotaCheckedAt: raw.QuotaCheckedAt ?? raw.quotaCheckedAt,
      IsStale: raw.IsStale ?? raw.isStale ?? false,
      ProviderRegistrationStatus: prs,
      FamilyQuotaMap: raw.FamilyQuotaMap ?? raw.familyQuotaMap ?? null,
      isRefreshing: false,
    };
  }
}
